/* eslint-disable @tanstack/query/exhaustive-deps */
import { datadogLogs } from '@datadog/browser-logs';
import {
  fetchGetApplication,
  fetchGetIntroducer,
  fetchGetStateByStateKey,
  GetApplicationResponse,
  GetDrawDownRepaymentScheduleCreditFacilityResponse,
  GetIntroducerApplicationOfferAllowedWithdrawalFeeRatesResponse,
  GetIntroducerApplicationOfferWithdrawalFeeRateResponse,
  GetIntroducerApplicationRejectOfferReasonsResponse,
  GetIntroducerContextResponse,
  GetIntroducerResponse,
  GetStateByStateKeyResponse,
} from '@iwoca/lapi-client/edge';
import {
  fetchGetApiIntroducerPortalIntroducerApplicationOfferAllowedWithdrawalFeeRates,
  fetchGetApiIntroducerPortalIntroducerApplicationOfferFundingRequirements,
  fetchGetApiIntroducerPortalIntroducerApplicationOfferWithdrawalFeeRate,
  fetchGetApiIntroducerPortalIntroducerApplicationRejectOfferReasons,
  fetchGetApiIntroducerPortalRepaymentSimulator,
} from '@iwoca/lapi-client/introducer_portal';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useStateKey } from 'components/Signup/hooks/useStateKey';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import { TCalculatorProducts } from 'types';
import { getCookie } from 'utils/cookieHelper';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useGetApplication } from './applications.hooks';
import { TFormalOffer, TSingleApplication } from './applications.types';
import {
  TGetApplicationResponseData,
  TGetOffersResponse,
  TGetStateResponseData,
} from './lapiOverrides.types';
import {
  lendingAPIGetDocuments,
  getIntroducerContext,
  fetchGetDrawDownRepaymentSchedule,
  fetchGetIntroducerPortalApiOffers,
} from './lendingApi';
import {
  TGetDocumentsResponse,
  TIntroducerApplicationFundingRequirements,
  TIntroducerRepaymentSimulatorResponse,
} from './lendingApi.types';

export const useQueryContext = () => {
  const isLoggedIn = getCookie('isLoggedIn') === 'True';
  const router = useRouter();
  return useQuery<GetIntroducerContextResponse>({
    queryKey: ['context', router.query?.__impersonate],
    queryFn: getIntroducerContext,
    enabled:
      router.pathname !== PATH_NAMES.register() &&
      !(router.pathname === PATH_NAMES.calculator() && !isLoggedIn),
  });
};

export const useQueryIntroducer = () => {
  const router = useRouter();
  const context = useQueryContext();
  const introducerId = context.data?.data.introducer_id;
  introducerId &&
    typeof window !== 'undefined' &&
    datadogLogs.setGlobalContextProperty(
      'introducer_id',
      introducerId.toString(),
    );

  const queryFn = async () => {
    const response = await fetchGetIntroducer({
      introducerId: introducerId as string,
    });

    if (!response.data?.introducer.is_active) {
      router.push(PATH_NAMES.forbidden());
    }

    return response;
  };

  const data = useQuery<GetIntroducerResponse>({
    queryKey: ['introducer', introducerId],
    queryFn,
    enabled: !!introducerId,
  });

  return {
    ...data,
    contextQuery: context,
    faultyImpersonation:
      context.error?.message === 'Forbidden' && '__impersonate' in router.query,
  };
};

export function useGetApplicationQuery() {
  const stateKey = useStateKey();

  const { data, status, refetch, isLoading } = useQuery<GetApplicationResponse>(
    {
      queryKey: ['fetchGetApplication', stateKey],
      queryFn: () => fetchGetApplication({ stateKey: stateKey as string }),
      enabled: Boolean(stateKey),
    },
  );

  return {
    application: (data?.data as TGetApplicationResponseData) || {},
    refetchApplication: refetch,
    applicationStatus: status,
    isLoading,
  };
}

export function useGetStateQuery<TResponse = TGetStateResponseData>() {
  const stateKey = useStateKey();

  const { data, status, refetch, isLoading } =
    useQuery<GetStateByStateKeyResponse>({
      queryKey: ['fetchGetState', stateKey],
      queryFn: () => fetchGetStateByStateKey({ stateKey: stateKey as string }),
      enabled: Boolean(stateKey),
    });

  return {
    state: data?.data as TResponse | undefined,
    refetchState: refetch,
    stateStatus: status,
    stateFetching: isLoading,
  };
}

export const useGetDocuments = (state_key?: string) => {
  return useQuery<TGetDocumentsResponse>({
    queryKey: ['documents', state_key],
    queryFn: () => lendingAPIGetDocuments(state_key as string),
    enabled: !!state_key,
  });
};

export const useGetOffers = (enabled: boolean, applicationId?: string) => {
  const introducerId = useIntroducerIdSelector();

  return useQuery<TGetOffersResponse>({
    queryKey: ['offers', applicationId],
    queryFn: () =>
      fetchGetIntroducerPortalApiOffers(introducerId, applicationId as string),
    enabled: Boolean(introducerId) && Boolean(applicationId) && enabled,
  });
};

export const useGetIntroducerFundingRequirements = () => {
  const { data } = useGetApplication();
  const introducerId = useIntroducerIdSelector();

  const applicationData = data?.data as TSingleApplication | undefined;
  const formalOffers = applicationData?.formal_offer_sets[0].formal_offers;

  const applicationId = applicationData?.application_uid as string;
  const offerId =
    formalOffers?.find((item: TFormalOffer) => item.is_selected)?.offer_id ||
    (formalOffers?.[0].offer_id as string);

  const queryFn = async () =>
    (
      await fetchGetApiIntroducerPortalIntroducerApplicationOfferFundingRequirements(
        { introducerId, applicationId, offerId },
      )
    ).json();

  return useQuery<TIntroducerApplicationFundingRequirements>({
    queryKey: ['fundingRequirements', introducerId, applicationId, offerId],
    queryFn,
    enabled:
      Boolean(introducerId) && Boolean(applicationId) && Boolean(offerId),
  });
};

export const useGetWithdrawalFeeRate = (offerId: string) => {
  const { data: applicationData } = useGetApplication();
  const introducerId = useIntroducerIdSelector();
  const applicationId = applicationData?.data?.application_uid;
  const productType =
    applicationData?.data?.requested_products?.[0].product_type;

  const queryFn = async () =>
    (
      await fetchGetApiIntroducerPortalIntroducerApplicationOfferWithdrawalFeeRate(
        { introducerId, applicationId, offerId },
      )
    ).json();

  return useQuery<GetIntroducerApplicationOfferWithdrawalFeeRateResponse>({
    queryKey: ['withdrawalFeeRate', applicationId],
    queryFn,
    enabled: !!applicationId && !!introducerId && productType === 'flexi24',
  });
};

export const useGetAllowedWithdrawalFee = (
  introducerId: string,
  applicationId: string,
  offerId: string,
  productType: string,
) => {
  const queryFn = async () =>
    (
      await fetchGetApiIntroducerPortalIntroducerApplicationOfferAllowedWithdrawalFeeRates(
        { introducerId, applicationId, offerId },
      )
    ).json();

  return useQuery<GetIntroducerApplicationOfferAllowedWithdrawalFeeRatesResponse>(
    {
      queryKey: ['allowedWithdrawalFees', offerId],
      queryFn,
      enabled:
        (!!applicationId &&
          !!introducerId &&
          !!offerId &&
          productType === 'flexi24') ||
        productType === 'credit_facility',
    },
  );
};

export const useGetRejectOfferReasons = (
  introducerId?: string,
  applicationId?: string,
  applicationStatus?: string,
) => {
  const queryFn = async () =>
    (
      await fetchGetApiIntroducerPortalIntroducerApplicationRejectOfferReasons({
        introducerId: introducerId as string,
        applicationId: applicationId as string,
      })
    ).json();

  return useQuery<GetIntroducerApplicationRejectOfferReasonsResponse>({
    queryKey: ['rejectOfferReasons', applicationId],
    queryFn,
    enabled:
      Boolean(applicationId) &&
      Boolean(introducerId) &&
      applicationStatus === 'offer_rejected',
  });
};

// Schedule for applications with an offer
export const useGetDrawdownRepaymentSchedule = (
  amount: number,
  formalOffer: TSingleApplication['formal_offer_sets'][number]['formal_offers'][number],
  withdrawalFeeRate?: number,
  date?: string,
) => {
  const { data: applicationData } = useGetApplication();
  const stateKey = applicationData?.data?.customer?.state_key;
  const productType = formalOffer.product_type;
  const offerId = formalOffer.offer_id;

  return useQuery<GetDrawDownRepaymentScheduleCreditFacilityResponse>({
    queryKey: [
      'drawdownRepaymentSchedule',
      amount,
      date,
      offerId,
      withdrawalFeeRate,
    ],
    queryFn: () =>
      fetchGetDrawDownRepaymentSchedule(
        productType,
        stateKey,
        offerId,
        amount,
        date,
      ),
    enabled: Boolean(stateKey) && Boolean(formalOffer),
  });
};

// Stand alone calculator schedule
export const useGetDrawdownRepaymentSimulator = ({
  product,
  interestRate,
  amount,
  duration,
  startRepaymentDate,
  endRepaymentDate,
  withdrawalFee,
  overpaymentAmount,
  maxTermLength,
  queryKey,
}: {
  product: TCalculatorProducts;
  interestRate: string;
  amount: string;
  duration: string;
  startRepaymentDate: string;
  endRepaymentDate: string | null;
  withdrawalFee?: string;
  maxTermLength: number;
  overpaymentAmount?: string;
  isDE: boolean;
  queryKey: (string | null | undefined)[];
}) => {
  interestRate = interestRate.replace(',', '.');
  const { trackEvent } = useAmplitude();
  const trimmedFundingFee = (Number(withdrawalFee) / 100).toFixed(2);
  const trimmedInterestRate = (Number(interestRate) / 100).toFixed(4);

  trackEvent(AMPLITUDE_EVENTS.calculatorFilterChanged, {
    Product: product,
    'Loan amount': amount,
    'Interest rate': interestRate,
    'Withdrawal fee': withdrawalFee || null,
    'Number of repayments': duration,
  });

  const queryFn = async () =>
    (
      await fetchGetApiIntroducerPortalRepaymentSimulator({
        query: {
          product,
          // @ts-ignore LAPI expects number but in query string so string fine
          initial_principal: amount,
          // @ts-ignore LAPI expects number but in query string so string fine
          '30_day_rate': trimmedInterestRate,
          loan_duration_months: maxTermLength,
          // @ts-ignore Apparently doesn't need to be a number because it can be undefined
          funding_fee: trimmedFundingFee,
          start_date: startRepaymentDate || undefined,
          early_final_repayment_date: endRepaymentDate || undefined,
          overpayment_repayment_amount: Number(overpaymentAmount) || undefined,
          promotions: undefined,
        },
      })
    ).json();

  return useQuery<TIntroducerRepaymentSimulatorResponse>({
    queryKey: ['repaymentSimulator', queryKey],
    queryFn,
    enabled: Number(amount) > 0,
  });
};

export const hasCbilsAvailableProduct = (
  introducerReponse: GetIntroducerResponse,
) =>
  introducerReponse.data?.introducer_company.available_products?.includes(
    'cbils',
  );

export const hasFlexiAvailableProduct = (
  introducerReponse: GetIntroducerResponse,
) =>
  introducerReponse.data?.introducer_company.available_products?.includes(
    'credit_facility',
  );

export const hasIwocaPayAvailableProduct = (
  introducerReponse: GetIntroducerResponse,
) =>
  introducerReponse.data?.introducer_company.available_products?.includes(
    'iwocapay',
  );

export const hasFlexi24AvailableProduct = (
  introducerReponse: GetIntroducerResponse,
  allowFlexi24?: boolean,
) =>
  introducerReponse.data?.introducer_company.available_products?.includes(
    'flexi24',
  ) || allowFlexi24;

export const hasMultipleOffersOnContext = (
  contextResponse: GetIntroducerContextResponse,
) => contextResponse?.data?.introducer_portal_multiple_formal_confirmed_offers;

export const hasTermLoansOnContext = (
  contextResponse: GetIntroducerContextResponse,
) => contextResponse?.data?.access_to_prime_loans;

export const getIntroducerId = (introducer: GetIntroducerContextResponse) =>
  introducer && introducer?.data?.introducer_id;

export const getIntroducerType = (introducerReponse: GetIntroducerResponse) =>
  introducerReponse &&
  introducerReponse?.data?.introducer_company?.introducer_type;

export const getAvailableProducts = (
  introducerReponse?: GetIntroducerResponse,
) => introducerReponse?.data?.introducer_company.available_products || [];

export const useIntroducerTypeSelector = () => {
  const { data: introducer } = useQueryIntroducer();
  return introducer?.data?.introducer_company.introducer_type;
};

export const useIntroducerIdSelector = () => {
  const { data } = useQueryContext();
  return getIntroducerId(data as GetIntroducerContextResponse);
};

export const useIntroducerContactEmail = () => {
  const router = useRouter();
  const { isDE } = useLocale();

  if (isDE) {
    return 'partner@iwoca.de';
  }
  if (router.query?.type === 'accountant') {
    return 'accountants@iwoca.co.uk';
  }
  return 'brokers@iwoca.co.uk';
};
